import { CdkDragDrop, CdkDragMove, DragDropModule } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  DashboardInstallationColumn,
  KanbanBoardHelperService,
  KanbanBoardStore,
  initialNumberOfInstallationsInColumn,
  limitOfSelfDefinedColumns,
} from '@store';
import { Subject, interval } from 'rxjs';
import { filter, takeUntil, withLatestFrom } from 'rxjs/operators';
import { LetDirective } from '@ngrx/component';

import { AnomalySeverity, DashboardInstallationData } from '@vi-iot/client';

import { ViLoaderComponent, ViUIService } from '@vi-iot/ui';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { MatInputModule } from '@angular/material/input';
import { FieldTextContentTooltipDirective } from '@vi-iot/ui/directives/tooltip/field-text-content-tooltip.directive';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxMaskModule } from 'ngx-mask';
import { CommonStandaloneModule } from '../../../shared/common-standalone.module';
import { KanbanInstallationListTileComponent } from '../kanban-installation-list-tile/kanban-installation-list-tile.component';
import { HorizontalScrollPace, ScrollDirection } from './kanban-installation-list-columns.helper';
import { KanbanColumnNamePipe } from './kanban-column-name/kanban-column-name.pipe';
import { KanbanColumnMenuComponent } from './kanban-column-menu/kanban-column-menu.component';
import { KanbanColumnNameInputComponent } from './kanban-column-name-input/kanban-column-name-input.component';
import { KanbanColumnDropCoverComponent } from './kanban-column-drop-cover/kanban-column-drop-cover.component';
import { LifecycleHooksHandlerDirective } from '../../../shared/directives/lifecycle-hooks-handler/lifecycle-hooks-handler.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavPanelData } from '../../../../projects/ui/services/ui.service-helper';

@Component({
  selector: 'app-kanban-installation-list-columns',
  standalone: true,
  imports: [
    CommonStandaloneModule,
    DragDropModule,
    ViLoaderComponent,
    MatIconModule,
    MatButtonModule,
    KanbanColumnNamePipe,
    KanbanColumnMenuComponent,
    KanbanInstallationListTileComponent,
    InfiniteScrollModule,
    FieldTextContentTooltipDirective,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    NgxMaskModule,
    FormsModule,
    KanbanColumnNameInputComponent,
    KanbanColumnDropCoverComponent,
    LetDirective,
    LifecycleHooksHandlerDirective,
    MatTooltipModule,
  ],
  templateUrl: './kanban-installation-list-columns.component.html',
  styleUrls: ['./kanban-installation-list-columns.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KanbanDashboardColumnsComponent implements OnInit, OnDestroy {
  @ViewChild('columnWrapper') columnWrapper: ElementRef<HTMLElement>;

  @Input() columns: DashboardInstallationColumn[];

  readonly horizontalScrollPixels = 5;
  readonly limitOfSelfDefinedColumns = limitOfSelfDefinedColumns;

  isDraggingColumn = false;
  isDraggingInstallation = false;
  idOfDraggedInstallation: number;
  indexOfColumnOfDraggedInstallation: number;
  scrollSpeed$: Subject<number> = new Subject();
  columnScrollTop: number;

  private ngUnsubscribe$: Subject<void> = new Subject();
  private isColumnHovered: boolean;
  private navPanelData: NavPanelData;

  mockedAnomalies = [
    [
      {
        id: '-1',
        diagnosisName: 'BLOCKING_HP_ERRORS',
        severity: AnomalySeverity.Information,
      },
    ],
    [
      {
        id: '0',
        diagnosisName: 'VERY_HIGH_NUMBER_OF_SOLAR_PUMP_EVENTS_WITH_DECREASING_DHW_TEMPERATURE',
        severity: AnomalySeverity.Warning,
      },
    ],
    [
      {
        id: '1',
        diagnosisName: 'SHORT_IGNITION_TIMES',
        severity: AnomalySeverity.Critical,
      },
    ],
  ];

  constructor(
    public kanbanBoardStore: KanbanBoardStore,
    private kanbanBoardHelperService: KanbanBoardHelperService,
    private viUiService: ViUIService
  ) {}

  ngOnInit(): void {
    this.viUiService.navPanelData$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((navPanelData) => {
        this.navPanelData = navPanelData;
      });
    this.adjustScrollingSpeed();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  onScroll(event: Event, column: DashboardInstallationColumn): void {
    if (column.loading || !column.canFetch) {
      return;
    }

    if (this.kanbanBoardHelperService.isNextActionOnInfiniteScroll(event)) {
      this.kanbanBoardStore.fetchInstallationsForColumn({
        columnId: column.id,
        limit: initialNumberOfInstallationsInColumn,
        offset: column.installations.length,
        withQuickView: true,
      });
    }
  }

  toggleColumnNameInput(column: DashboardInstallationColumn): void {
    if (column.selfDefined && !column.inputShowed) {
      this.kanbanBoardStore.manageColumnNameInput(column.id, true);
    }
  }

  addNewColumn(): void {
    this.kanbanBoardStore.addTempColumn();
  }

  trackByColumn(_index: number, column: DashboardInstallationColumn): number {
    return column.id;
  }

  trackByInstallation(_index: number, installation: DashboardInstallationData): number {
    return installation.id;
  }

  columnDrop(data: CdkDragDrop<unknown, unknown, unknown>): void {
    const movedColumnId = this.columns[data.previousIndex].id;
    const targetColumnId = this.columns[data.currentIndex].id;

    if (movedColumnId === targetColumnId) {
      return;
    }

    this.kanbanBoardStore.moveKanbanColumn({
      movedColumnId,
      movedColumnIndex: data.previousIndex,
      targetColumnId,
      targetColumnIndex: data.currentIndex,
    });
  }

  installationDrop(
    data: CdkDragDrop<
      { column: DashboardInstallationColumn },
      { column: DashboardInstallationColumn },
      { data: DashboardInstallationData }
    >
  ): void {
    if (!this.isColumnHovered) {
      return;
    }

    const previousColumnId = data.previousContainer.data.column.id;
    const nextColumnId = data.container.data.column.id;
    const index = data.item.element.nativeElement.attributes['data-index'].value;
    const installation = data.previousContainer.data.column.installations?.[+index];

    const droppedInSameColumn = previousColumnId === nextColumnId;
    const droppedInFixedColumn = !data.container.data.column.selfDefined;
    if (droppedInSameColumn || droppedInFixedColumn) {
      return;
    }
    const isInstallationAlreadyCopied = this.columns
      .find((column) => column.id === nextColumnId)
      .installations.some((el) => el.id === installation.id);
    if (isInstallationAlreadyCopied) {
      return;
    }

    const isPreviousColumnSelfDefined = data.previousContainer.data.column.selfDefined;
    if (isPreviousColumnSelfDefined) {
      this.kanbanBoardStore.moveKanbanInstallation(installation, nextColumnId, previousColumnId);
    } else {
      this.kanbanBoardStore.copyKanbanInstallation(installation, nextColumnId, previousColumnId);
    }
  }

  columnDragStart(): void {
    this.isDraggingColumn = true;
  }
  columnDragStop(): void {
    this.isDraggingColumn = false;
  }

  installationDragStart(index: number, installationId: number): void {
    this.idOfDraggedInstallation = installationId;
    this.isDraggingInstallation = true;
    this.indexOfColumnOfDraggedInstallation = index;
  }

  scrollColumn(column: HTMLDivElement, tileClientHeight: number): void {
    const event = (<unknown>{ target: column }) as Event;
    const isNearBottom = this.kanbanBoardHelperService.isNextActionOnInfiniteScroll(
      event,
      tileClientHeight
    );
    if (isNearBottom) {
      column.scrollTop = this.columnScrollTop - tileClientHeight;
    } else {
      column.scrollTop = this.columnScrollTop;
    }
  }

  installationDragStop(): void {
    this.isDraggingInstallation = false;
    this.idOfDraggedInstallation = null;
    this.indexOfColumnOfDraggedInstallation = null;
  }

  handleDragEvent(event: CdkDragMove): void {
    const scrollDirection: ScrollDirection =
      event.pointerPosition.x > window.innerWidth / 2 ? 'right' : 'left';
    const pace = this.getPaceAccordingToMousePosition(event.pointerPosition.x, scrollDirection);
    const scrollValue = scrollDirection === 'right' ? pace : pace * -1 || undefined;

    this.scrollSpeed$.next(scrollValue);
  }

  setIsColumnHovered(value: boolean): void {
    this.isColumnHovered = value;
  }

  private adjustScrollingSpeed(): void {
    interval(20)
      .pipe(
        withLatestFrom(this.scrollSpeed$),
        filter(
          (val) => (this.isDraggingColumn || this.isDraggingInstallation) && val !== undefined
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([_interval, pace]) => {
        this.columnWrapper.nativeElement.scrollBy(pace, 0);
      });
  }

  setColumnScrollTop(scrollTopValue: number): void {
    this.columnScrollTop = scrollTopValue;
  }

  private getPaceAccordingToMousePosition(
    horizontalPixel: number,
    direction: ScrollDirection
  ): number {
    const windowWidth = window.innerWidth;
    let measuredDistance = direction === 'left' ? horizontalPixel : windowWidth - horizontalPixel;
    if (this.navPanelData.isOpen) {
      measuredDistance -= this.navPanelData.width;
    }

    if (measuredDistance >= 45 && measuredDistance < 60) {
      return HorizontalScrollPace.Pace_1;
    } else if (measuredDistance >= 30 && measuredDistance < 45) {
      return HorizontalScrollPace.Pace_2;
    } else if (measuredDistance >= 15 && measuredDistance < 30) {
      return HorizontalScrollPace.Pace_3;
    } else if (measuredDistance < 15) {
      return HorizontalScrollPace.Pace_4;
    }
  }
}
